/**
 * SharePoint Drive Configuration Constants
 * 
 * These constants are used as keys for mapping to backend SharePoint drive tables.
 * The backend service (RPlusData/src/routes/fileDrive/utils.ts) uses these identifiers
 * to resolve the actual SharePoint site and root drive path for each user/context.
 * 
 * Backend Mapping Example:
 * - ldd_fake_drive_root → { driveSite: `f${userId}`, rootDrive: "client files" }
 * - ldd_fake_drive_tenant → Replaced with actual Microsoft Graph API tenant URL
 * 
 * DO NOT change these values without coordinating with backend service mappings.
 */

/**
 * Root drive identifiers used across the application
 */
export const SHAREPOINT_ROOT_DRIVES = {
  /**
   * Client files drive - Primary storage for client documents
   * Backend maps to: { driveSite: `f${userId}`, rootDrive: "client files" }
   */
  CLIENT_FILES: 'ldd_fake_drive_root',
  
  /**
   * User configuration drive - User-specific app settings
   * Backend maps to: { driveSite: `f${userId}`, rootDrive: `configuration/${appCode}` }
   */
  USER_CONFIG: 'ldd_userconfig_root',
  
  /**
   * Public master documents - Firm-wide master templates
   * Backend maps to: { driveSite: "publicmasters", rootDrive: `${appCode}/master documents` }
   */
  PUBLIC_MASTER: 'ldd_publicmaster_root',
  
  /**
   * User master documents - User-specific master templates
   * Backend maps to: { driveSite: `f${userId}`, rootDrive: `Configuration/${appCode}/master documents` }
   */
  USER_MASTER: 'ldd_usermaster_root',
} as const;

/**
 * SharePoint tenant/host identifiers
 */
export const SHAREPOINT_HOSTS = {
  /**
   * Fake tenant placeholder - Replaced by backend with actual Microsoft Graph API URL
   * Backend replaces with: `${graphURL}${driveID}/drive`
   */
  LDD_TENANT: 'ldd_fake_drive_tenant',
  
  /**
   * Alternative tenant placeholder (legacy)
   * Backend replaces with: `${graphURL}${driveID}/drive`
   */
  LDD_DRIVE_TENANT: 'ldd_drive_tenant',
} as const;

/**
 * Type definitions for type-safe usage
 */
export type SharePointRootDrive = typeof SHAREPOINT_ROOT_DRIVES[keyof typeof SHAREPOINT_ROOT_DRIVES];
export type SharePointHost = typeof SHAREPOINT_HOSTS[keyof typeof SHAREPOINT_HOSTS];

/**
 * Helper to create standard SharePoint config object
 * @param appId - identification for the file number 
 * @param provinceCode - Application province/code
 * @param firmId - lawyer or firm id
 * @param sessionId - Session ID
 * @param endpoint - Endpoint for which backend service
 * @param rootDrive - Root drive identifier (default: CLIENT_FILES)
 * @param host - Host identifier (default: LDD_TENANT)
 * @returns SharePoint configuration object
 */
export function createSharePointConfig({
  appId,
  provinceCode,
  firmId,
  sessionId,
  endpoint,
  rootDrive = SHAREPOINT_ROOT_DRIVES.CLIENT_FILES,
  host = SHAREPOINT_HOSTS.LDD_TENANT,
}: {
  appId: string;
  provinceCode: string;
  firmId: string;
  sessionId: string;
  endpoint: string;
  rootDrive?: SharePointRootDrive;
  host?: SharePointHost;
}) {
  return {
    rootDrive,
    host,
    urlOverwrite: `/${endpoint}/api/${provinceCode}/${firmId}/${appId}/filedrive/sendToSharepoint?SessionID=${sessionId}`,
    AppID: appId,
    AppCode: provinceCode,
    UserID: firmId,
  };
}

/**
 * Export individual constants for direct usage (legacy compatibility)
 */
export const LDD_FAKE_DRIVE_ROOT = SHAREPOINT_ROOT_DRIVES.CLIENT_FILES;
export const LDD_FAKE_DRIVE_TENANT = SHAREPOINT_HOSTS.LDD_TENANT;

